<div *ngIf="isLoading" class="loader-container">
  <app-loading></app-loading>
</div>

<div class="app-dialog" *ngIf="!isLoading && employee">
  <header class="app-dialog__header">
    <div class="app-dialog__header-text">
      <h2>{{ employee.fullName }} ({{ employee.email }})</h2>
    </div>
    <div class="app-dialog__header-actions">
      <ng-container *ngIf="canForceSyncEmployee(employee.dataSource)">
        <span class="data-source-type">{{ getEmployeeDataSourceLabel(employee.dataSource) | translate }}</span>
        <button
          mat-stroked-button
          type="button"
          (click)="forceSyncEmployee()"
          [disabled]="isSyncing"
        >
          <mat-spinner
            diameter="20"
            *ngIf="isSyncing"
            class="dc-btn-spinner"
          ></mat-spinner>
          <mat-icon *ngIf="!isSyncing">sync</mat-icon>
          {{ 'employee.forceSync' | translate }}
        </button>
      </ng-container>
      <button
        *ngIf="auth.appUser?.optionalInterfaces?.digitalCard && !employee?.excludedFromSignatures"
        mat-stroked-button
        type="button"
        (click)="openDigitalCard()"
      >
        <mat-icon>qr_code_2</mat-icon>
        {{ 'employee.digitalCard' | translate }}
      </button>
    </div>
    <button mat-icon-button type="button" (click)="cancel()" [attr.aria-label]="'cancel' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <div class="app-dialog__body">
    <div class="employee-edit-dialog__layout">
      <div class="main-content">
        <section class="app-dialog__section form-section" *ngIf="employee?.signature || employee?.signatureInternal">
          <header class="app-dialog__section-head">
            <h3>{{ 'employee.signaturePreview' | translate }}</h3>
          </header>
          <div *ngIf="employee?.signature && employee?.signatureInternal" class="signature-toggle">
            <mat-button-toggle-group [(ngModel)]="previewType" (ngModelChange)="updateParsedSignature()" aria-label="Signature preview type">
              <mat-button-toggle value="external">{{ 'externalSignature' | translate }}</mat-button-toggle>
              <mat-button-toggle value="internal">{{ 'internalSignature' | translate }}</mat-button-toggle>
            </mat-button-toggle-group>
          </div>
          <div class="signature-preview" *ngIf="parsedSignature">
            <div class="signature-wrapper" #previewContainer>
              <div [innerHTML]="parsedSignature | safeHtml" class="signature-content" appScaleContent [appScaleContent]="previewContainer"></div>
            </div>
            <div class="signature-actions">
              <button mat-stroked-button type="button" (click)="copySignaturePlainToClipboard()">
                <mat-icon>content_copy</mat-icon>
                <span>{{ 'employee.copySignaturePlain' | translate }}</span>
              </button>
              <button mat-stroked-button type="button" (click)="copySignatureToClipboard()">
                <mat-icon>content_copy</mat-icon>
                <span>{{ 'employee.copySignature' | translate }}</span>
              </button>
              <button mat-stroked-button type="button" (click)="downloadSignatureOutlook()">
                <mat-icon>download</mat-icon>
                <span>{{ 'employee.downloadSignatureOutlook' | translate }}</span>
              </button>
            </div>
          </div>
        </section>

        <form [formGroup]="form" class="form" (ngSubmit)="save()">
          <section class="app-dialog__section form-section">
            <header class="app-dialog__section-head">
              <h3>{{ 'employee.data' | translate }}</h3>
            </header>
            <div class="data-grid">
              <div class="data-wrapper">
                <label>{{ 'employee.createdAt' | translate }}:</label>
                <span>{{ employee.createdAt | localizedDate:'medium' }}</span>
              </div>

              <div class="data-wrapper">
                <label>{{ 'employee.modifiedAt' | translate }}:</label>
                <span>{{ employee.modifiedAt | localizedDate:'medium' }}</span>
              </div>

              <div class="data-wrapper" *ngIf="employee.signatureInstallationAt">
                <label>{{ 'employee.signatureInstallationAt' | translate }}:</label>
                <span>{{ employee.signatureInstallationAt | localizedDate:'medium' }}</span>
              </div>

              <div class="data-wrapper radio-wrapper">
                <label>{{ 'employee.excludedFromSignatures' | translate }}:</label>
                <mat-radio-group formControlName="excludedFromSignatures"
                                [disabled]="!employee.canBeExcludedFromSignatures"
                                [matTooltip]="employee.canBeExcludedFromSignatures ? ('employee.excludedFromSignaturesTooltip' | translate) : ('employee.cannotBeExcludedFromSignatures' | translate)">
                  <mat-radio-button [disabled]="!employee.canBeExcludedFromSignatures" [value]="true">
                    {{ 'yes' | translate }}
                  </mat-radio-button>
                  <mat-radio-button [value]="false">
                    {{ 'no' | translate }}
                  </mat-radio-button>
                  <mat-error *ngIf="form.hasError('required', ['excludedFromSignatures'])">
                    {{ 'fieldRequired' | translate }}
                  </mat-error>
                </mat-radio-group>
              </div>
            </div>
          </section>

          <section class="app-dialog__section form-section">
            <header class="app-dialog__section-head">
              <h3>{{ 'employee.details' | translate }}</h3>
            </header>
            <div class="fields-grid">
              <mat-form-field appearance="outline">
                <mat-label>{{ profileFieldLabel('email') }}</mat-label>
                <input matInput formControlName="email" type="text" />
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ profileFieldLabel('givenName') }}</mat-label>
                <input matInput formControlName="givenName" type="text" [errorStateMatcher]="errorStateMatcher">
                <mat-error *ngIf="form.hasError('required', ['givenName'])">
                  {{ 'fieldRequired' | translate }}
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ profileFieldLabel('familyName') }}</mat-label>
                <input matInput formControlName="familyName" type="text" [errorStateMatcher]="errorStateMatcher">
                <mat-error *ngIf="form.hasError('required', ['familyName'])">
                  {{ 'fieldRequired' | translate }}
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{ profileFieldLabel('userAbout') }}</mat-label>
                <textarea matInput formControlName="userAbout" rows="3" [errorStateMatcher]="errorStateMatcher"></textarea>
                <mat-error *ngIf="form.hasError('required', ['userAbout'])">
                  {{ 'fieldRequired' | translate }}
                </mat-error>
              </mat-form-field>

              <div [formGroup]="dynamicVariables" class="dynamic-fields">
                <mat-form-field appearance="outline" *ngFor="let employSetting of dynamicEmployeeSettings">
                  <mat-label>{{ employSetting.label || ('employee.signatureVariables.' + employSetting.name | translate) }}</mat-label>
                  <input matInput [formControlName]="employSetting.name" type="text" [errorStateMatcher]="errorStateMatcher">
                  <mat-error *ngIf="dynamicVariables.hasError('required', [employSetting.name])">
                    {{ 'fieldRequired' | translate }}
                  </mat-error>
                  <mat-error *ngIf="dynamicVariables.hasError('phoneNumberInvalid', [employSetting.name])">
                    {{ 'phoneNumberInvalid' | translate }}
                  </mat-error>
                </mat-form-field>
              </div>
            </div>
          </section>

          <app-employee-branch-section
            [branchId]="branchId"
            [employeeBranchVariables]="branchVariables"
            [errorStateMatcher]="errorStateMatcher"
            (branchChange)="onBranchChange($event)">
          </app-employee-branch-section>

          <app-employee-vcard
            [parentForm]="form"
            [entries]="vcardEntries"
            [loadVersion]="vcardLoadVersion"
            [errorStateMatcher]="errorStateMatcher"
            (changed)="form.markAsDirty()"
          ></app-employee-vcard>

          <section class="app-dialog__section form-section" *ngIf="showNameProfile">
            <header class="app-dialog__section-head">
              <h3>
                {{ 'employee.nameProfile' | translate }}
                <span class="dbc1-inactive-tag" *ngIf="nameProfileInactive">{{ 'menu.inactive' | translate }}</span>
              </h3>
            </header>
            <div class="fields-grid">
              <mat-form-field appearance="outline">
                <mat-label>{{ 'employee.gender' | translate }}</mat-label>
                <mat-select formControlName="gender">
                  <mat-option [value]="null">{{ 'employee.genderUnspecified' | translate }}</mat-option>
                  <mat-option *ngFor="let option of genderOptions" [value]="option.value">
                    {{ option.labelKey | translate }}
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <app-country-select
                formControlName="countryOfOrigin"
                [label]="'employee.countryOfOrigin' | translate"
                [errorStateMatcher]="errorStateMatcher"
              ></app-country-select>
            </div>
          </section>

          <section class="app-dialog__section form-section" *ngIf="advancedSettings?.badgesEnabled">
            <header class="app-dialog__section-head">
              <h3>{{ 'badges.title' | translate }}</h3>
            </header>
            <app-badges
              [badges]="availableBadges"
              [(selected)]="selectedBadges"
              (selectedChange)="form.markAsDirty()">
            </app-badges>
          </section>
        </form>
      </div>

      <div class="side-content">
        <section class="app-dialog__section form-section">
          <header class="app-dialog__section-head">
            <h3>{{ 'employee.profilePicture' | translate }}</h3>
          </header>
          <div class="profile-picture-wrapper">
            <app-avatar
              [avatarUrl]="displayAvatarUrl"
              [avatarUploadUrl]="'/employees/' + employee.employeeId + '/avatar'"
              [localAvatar]="employee.localAvatar"
              [size]="120"
              [employeeId]="employee.employeeId"
              (avatarChanged)="onAvatarChanged()">
            </app-avatar>
            <button
              *ngIf="employee.picture && !pendingRemoveAvatar"
              type="button"
              class="remove-photo-btn"
              [matTooltip]="'employee.avatar.removePhoto' | translate"
              [attr.aria-label]="'employee.avatar.removePhoto' | translate"
              (click)="removeAvatar()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>

  <footer class="app-dialog__footer">
    <button mat-stroked-button type="button" (click)="cancel()">
      <mat-icon>cancel</mat-icon>
      <span>{{ 'cancel' | translate }}</span>
    </button>
    <button *ngIf="employee.signature || employee.signatureInternal" mat-stroked-button color="primary" type="button" (click)="saveAndReinstall()"
      [disabled]="!form.dirty || isLoading || saving">
      <mat-spinner *ngIf="saving" diameter="20" class="dc-btn-spinner"></mat-spinner>
      <mat-icon *ngIf="!saving">install_desktop</mat-icon>
      <span>{{ saving ? ('saving' | translate) : ('saveAndReinstall' | translate) }}</span>
    </button>
    <button mat-raised-button color="primary" type="button" (click)="save()"
      [disabled]="!form.dirty || isLoading || saving">
      <mat-spinner *ngIf="saving" diameter="20" class="dc-btn-spinner"></mat-spinner>
      <mat-icon *ngIf="!saving">save</mat-icon>
      <span>{{ saving ? ('saving' | translate) : ('save' | translate) }}</span>
    </button>
  </footer>
</div>
